module Widgets.ExpectationPicker
    (ExpectationPicker, ExpectationsQuery
    , ExpectationPickerTab, ExpectationPickerTabBase
    , expectationPicker, initExpectationPicker, selectedExpectations
    , rankFilterEntry
    )
    where

import Common

import API.Expectations (ExpectationBase, ExpectationFilterEntry)
import API.Pagination (Page, changePage, pageItems)
import API.Types (ID, ExpectationID)
import Concur.Core (Widget)
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Alt ((<|>))
import Data.Argonaut (class DecodeJson)
import Data.Array (fromFoldable, groupBy, mapWithIndex)
import Data.Array as Arr
import Data.Array.NonEmpty (NonEmptyArray)
import Data.Array.NonEmpty as ArrNE
import Data.Either (isRight)
import Data.List (List)
import Data.Maybe (fromJust)
import Effect.Class (liftEffect)
import Effect.Console (log)
import GraphQL.Type (innerId)
import Internal.SelectionMap (SelectionMap, selectAllCheckbox')
import Internal.SelectionMap as Sel
import Partial.Unsafe (unsafePartial)
import Widget.Pager (pagerWidget)
import Widgets.BetterSelect (Select, SelectOption, rankByLabel, selectWidget, selectedItem)
import Widgets.Checkbox (labelCheckbox)
import Widgets.Modal (ModalResult(..), loopModal)


type ExpectationPicker e =
    { selection :: SelectionMap ExpectationID (ExpectationBase e)
    , tabs :: Array (ExpectationPickerTab e)
    , activeTab :: Int
    }

type ExpectationPickerInit e =
    { tabs :: Array (ExpectationPickerTabBase e ())
    }

selectedExpectations :: forall e. ExpectationPicker e -> List (ExpectationBase e)
selectedExpectations state = Sel.listSelected state.selection


-- A tab within the expectation picker.
type ExpectationPickerTabBase e r =
    { label :: String
    , query :: ExpectationsQuery e
    , select :: Select ExpectationFilterEntry
    | r
    }

type ExpectationPickerTab e = ExpectationPickerTabBase e
    ( page :: Either String (Page (ExpectationBase e)) )

type ExpectationsQuery e = ID -> Aff (Either ErrorWrapper (Page (ExpectationBase e)))

rankFilterEntry :: String -> SelectOption ExpectationFilterEntry -> Int
rankFilterEntry = rankByLabel


data ExpectationPickerAction e
    = ChangeTab Int
    | CourseChange (Select ExpectationFilterEntry)
    | Selection (SelectionMap ExpectationID (ExpectationBase e))
    | ChangePage Int
    | Done


-- | Initializes the state of the expectation picker modal.
initExpectationPicker :: forall e. ExpectationPickerInit e -> ExpectationPicker e
initExpectationPicker init =
    { selection: Sel.empty, tabs, activeTab: 0 }
  where
    tabs = map tab init.tabs
    tab { label, query, select } = { label, query, page: Left "", select }


-- | Modal which allows the user to choose expectations to add to a form.
expectationPicker :: forall e. DecodeJson (ExpectationBase e) => ExpectationPicker e -> Widget HTML (ExpectationPicker e)
expectationPicker = loopModal (D.div' []) runExpectationPicker

runExpectationPicker :: forall e. DecodeJson (ExpectationBase e) => ExpectationPicker e -> Widget HTML (ModalResult (ExpectationPicker e))
runExpectationPicker state = handleAction state =<< renderWidget state


handleAction :: forall e. DecodeJson (ExpectationBase e)
    => ExpectationPicker e
    -> ExpectationPickerAction e
    -> Widget HTML (ModalResult (ExpectationPicker e))

handleAction state (ChangeTab idx) | idx >= 0 && idx < Arr.length state.tabs = do
    let state' = state { activeTab = idx }
    let curTab = currentTab state'
    if isRight curTab.page
        then pure $ ModalReturned state'
        else case selectedItem curTab.select of
            Just item -> loadExpectations state' item.value.id
            Nothing -> pure $ ModalReturned state'

handleAction state (ChangeTab idx) = do
    liftEffect $ log ("Warning: tab index out of range: " <> show idx)
    pure $ ModalReturned state

handleAction state (CourseChange sel) = do
    newstate <- updateTab state \tab -> pure $ tab { select = sel }
    case selectedItem sel of
        Just item -> loadExpectations newstate item.value.id
        Nothing -> pure $ ModalReturned newstate

handleAction state (ChangePage idx) = loadPageChange state idx

handleAction state (Selection sel) =
    pure $ ModalReturned $ state { selection = sel }

handleAction _ Done = pure ModalClosed


data ExpLoadAction e a
    = EPAction (ExpectationPickerAction e)
    | LoadResult a

doLoadAction :: forall e. DecodeJson (ExpectationBase e) => ExpectationPicker e
    -> Aff (Either ErrorWrapper (Page (ExpectationBase e)))
    -> (Either ErrorWrapper (Page (ExpectationBase e)) -> Widget HTML (ExpectationPicker e))
    -> Widget HTML (ModalResult (ExpectationPicker e))
doLoadAction state action handle = do
    loadState <- updateTab state \tab -> pure $ tab { page = Left "Loading..." }
    act <- (EPAction <$> renderWidget loadState) <|>
           (LoadResult <$> affAction action)
    case act of
        EPAction a -> handleAction state a
        LoadResult r -> ModalReturned <$> handle r

loadExpectations :: forall e. DecodeJson (ExpectationBase e) => ExpectationPicker e -> ID -> Widget HTML (ModalResult (ExpectationPicker e))
loadExpectations state selected =
    doLoadAction state ((currentTab state).query selected) (handle' state)

handle' :: forall e. ExpectationPicker e
    -> Either ErrorWrapper (Page (ExpectationBase e))
    -> Widget HTML (ExpectationPicker e)
handle' st (Right exps) = updateTab st \tab -> pure $ tab { page = Right exps }
handle' st (Left err) =
    updateTab st \tab -> pure $ tab { page = Left ("Error loading expectations: " <> show err) }

loadPageChange :: forall e. DecodeJson (ExpectationBase e) => ExpectationPicker e -> Int -> Widget HTML (ModalResult (ExpectationPicker e))
loadPageChange state idx = case (currentTab state).page of
    Right page -> doLoadAction state (changePage idx page) (handle' state)
    Left _ -> pure $ ModalReturned state

renderWidget :: forall e. ExpectationPicker e -> Widget HTML (ExpectationPickerAction e)
renderWidget state = D.div
    [ P.className "expectation-picker modal-grid"
    , P._id "expectation-picker" ]
    [ tabs, curTabWidget state, doneBtn ]
  where
    doneBtn =
        D.div
            [ P.className "expectation-picker-buttons modal-buttons" ]
            [ D.button
                [ P._type "button", P.className "btn btn-primary"
                , Done <$ P.onClick
                ]
                [ D.text $ lookupTranslation "done" ]
            ]

    tabs = D.nav [P.className "tabs"] $ mapWithIndex tab state.tabs
    tab idx t = D.a
        [ P.className $ "tab" <> if state.activeTab == idx then " active" else ""
        , P.href "javascript:void(0);"
        , ChangeTab idx <$ P.onClick ]
        [ D.text t.label ]

curTabWidget :: forall e. ExpectationPicker e -> Widget HTML (ExpectationPickerAction e)
curTabWidget state = D.div
    [ P.className "expectation-picker-body" ]
    [ select' state
    , D.div [ P.className "expectation-pager" ] [ exDiv state $ currentTab state ]
    ]

select' :: forall e. ExpectationPicker e -> Widget HTML (ExpectationPickerAction e)
select' state = CourseChange <$> selectWidget (currentTab state).select

exDiv :: forall e. ExpectationPicker e -> ExpectationPickerTab e -> Widget HTML (ExpectationPickerAction e)
exDiv _ { page: Left err } = D.div' [D.p' [D.text err]]
exDiv state { page: Right page } =
    D.div [ P.className "expectation-pager" ] [ exList page state, pager page ]

pager :: forall e. Page (ExpectationBase e) -> Widget HTML (ExpectationPickerAction e)
pager page = ChangePage <$> pagerWidget page

exList :: forall e. Page (ExpectationBase e) -> ExpectationPicker e -> Widget HTML (ExpectationPickerAction e)
exList page state = D.div [ P.className "expectation-list" ] $
    [allCheck page state] <> map (exCategory state) grouped
  where
    grouped = groupBy (\a b -> a.category == b.category) $ pageItems page

exCategory :: forall e. ExpectationPicker e -> NonEmptyArray (ExpectationBase e)
    -> Widget HTML (ExpectationPickerAction e)
exCategory state exps = D.div
    [ P.className "expectation-category" ] $
    (case category of
        Just catname -> [ D.h1' [ D.text catname ] ]
        Nothing -> [])
    <>
    (map (exSubCategory state) grouped)
  where
    grouped = groupBy (\a b -> a.sub_category == b.sub_category) $ fromFoldable exps
    category = (ArrNE.head exps).category

exSubCategory :: forall e. ExpectationPicker e -> NonEmptyArray (ExpectationBase e)
    -> Widget HTML (ExpectationPickerAction e)
exSubCategory state exps = D.div
    [ P.className "expectation-category" ] $
    (case category of
        Just catname -> [ D.h2' [ D.text catname ] ]
        Nothing -> [])
    <>
    (map (exListItem state) $ fromFoldable exps)
  where
    category = (ArrNE.head exps).sub_category

exListItem :: forall e. ExpectationPicker e -> ExpectationBase e -> Widget HTML (ExpectationPickerAction e)
exListItem state exp = expectationCheck state exp

allCheck :: forall e. Page (ExpectationBase e) -> ExpectationPicker e -> Widget HTML (ExpectationPickerAction e)
allCheck page state = Selection <$>
    selectAllCheckbox' (pageItems page)
        (labelCheckbox "expectations-select-all" $ lookupTranslation "select-all")
        state.selection

expectationCheck :: forall e. ExpectationPicker e -> ExpectationBase e -> Widget HTML (ExpectationPickerAction e)
expectationCheck state exp = Selection <$>
    Sel.selectionCheckbox exp.id exp (labelCheckbox (innerId exp.id) exp.description) state.selection

currentTab :: forall e. ExpectationPicker e -> ExpectationPickerTab e
-- We check elsewhere to ensure this index is in range.
currentTab state = unsafePartial $ fromJust $ Arr.index state.tabs state.activeTab

updateTab :: forall e. ExpectationPicker e
    -> (ExpectationPickerTab e -> Widget HTML (ExpectationPickerTab e))
    -> Widget HTML (ExpectationPicker e)
updateTab state update = do
    newtab <- update $ currentTab state
    case Arr.updateAt state.activeTab newtab state.tabs of
        Just newtabs -> pure $ state { tabs = newtabs }
        Nothing -> do
           -- FIXME: This should never happen but maybe we should report this
           -- error better just in case.
           liftEffect $ log ("Expectation picker current tab index out of bounds!!")
           pure state
